import { formatDateTime, type LatLng, type RouteTrack } from '@fdt/shared';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';

/** Playback speeds, as multiples of real time. */
const SPEEDS = [60, 120, 300, 600];

export interface PlaybackState {
  /** Index into the track's samples, or null when nothing is loaded. */
  index: number | null;
  cursor: LatLng | null;
}

/**
 * Scrubbing through a recorded track.
 *
 * The cursor is an **index into the samples**, not a clock. A track is not
 * evenly spaced in time — a van standing at a customer produces a sample every
 * fifteen seconds and one driving produces the same number over a kilometre —
 * so stepping by index moves through what was recorded, and the timestamp of
 * the current sample is shown so the viewer knows when they are.
 *
 * The parent owns the cursor, because the map draws it.
 */
export function RoutePlayback({
  track,
  onCursorChange,
}: {
  track: RouteTrack;
  onCursorChange: (state: PlaybackState) => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(SPEEDS[1]!);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const last = Math.max(0, track.pings.length - 1);
  const current = track.pings[Math.min(index, last)];

  // The same ranges the map colours, so clicking a deviation in the list lands
  // on the stretch the map is highlighting.
  const deviationRanges = useMemo(() => deviationRangesOf(track), [track]);

  useEffect(() => {
    onCursorChange({
      index: track.pings.length === 0 ? null : Math.min(index, last),
      cursor: current?.location ?? null,
    });
    // `onCursorChange` is a fresh closure on every parent render; depending on
    // it would restart this effect constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, last, track]);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    // One step per sample, at a tick rate derived from the chosen multiple of
    // real time and the track's own average spacing.
    const spanSeconds =
      track.firstAt && track.lastAt
        ? (Date.parse(track.lastAt) - Date.parse(track.firstAt)) / 1000
        : 0;
    const perSample = track.pings.length > 1 ? spanSeconds / (track.pings.length - 1) : 1;
    const tickMs = Math.max(40, (perSample / speed) * 1000);

    timerRef.current = setInterval(() => {
      setIndex((value) => {
        if (value >= last) {
          setPlaying(false);
          return last;
        }
        return value + 1;
      });
    }, tickMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [playing, speed, last, track]);

  if (track.pings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('tracking.noPlayback')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {track.planChangedAfterTrack && <Alert severity="warning">{t('tracking.planChanged')}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton
          onClick={() => {
            // Restarting from the end rather than sitting there doing nothing.
            if (index >= last) setIndex(0);
            setPlaying((value) => !value);
          }}
          color="primary"
          aria-label={t(playing ? 'tracking.pause' : 'tracking.play')}
        >
          {playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <Typography sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 170 }}>
          {current ? formatDateTime(current.recordedAt, locale) : '—'}
        </Typography>

        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <Slider
            value={Math.min(index, last)}
            min={0}
            max={last}
            onChange={(_, value) => {
              setPlaying(false);
              setIndex(Array.isArray(value) ? (value[0] ?? 0) : value);
            }}
            aria-label={t('tracking.playback')}
          />
        </Box>

        <TextField
          select
          size="small"
          label={t('tracking.speedUp')}
          value={speed}
          onChange={(event) => setSpeed(Number(event.target.value))}
          sx={{ width: 110 }}
        >
          {SPEEDS.map((value) => (
            <MenuItem key={value} value={value}>
              ×{value}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`${t('tracking.samples')}: ${track.pings.length}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${t('tracking.recordedDistance')}: ${(track.distanceMeters / 1000).toFixed(1)} km`}
          variant="outlined"
        />
        {current?.speedMetersPerSecond !== null && current?.speedMetersPerSecond !== undefined && (
          <Chip
            size="small"
            label={`${t('tracking.speed')}: ${Math.round(current.speedMetersPerSecond * 3.6)} km/h`}
            variant="outlined"
          />
        )}
        {/* Shown plainly, not as a warning. A large positive gap means the phone
            was offline and uploaded a backlog, which is normal and is exactly
            what the queue is for; only a device clock *ahead* of the server
            would indicate something wrong, and the server refuses those
            samples outright. */}
        {track.clockSkewSeconds !== null && track.clockSkewSeconds > 60 && (
          <Chip
            size="small"
            variant="outlined"
            label={t('tracking.clockSkew', { seconds: track.clockSkewSeconds })}
          />
        )}
      </Stack>

      {track.deviations === null ? (
        <Typography variant="body2" color="text.secondary">
          {t('tracking.noPlanForDeviation')}
        </Typography>
      ) : track.deviations.length === 0 ? (
        <Typography variant="body2" color="success.main">
          {t('tracking.noDeviations')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
            {t('tracking.deviationCount', { count: track.deviations.length })}
          </Typography>
          {track.deviations.map((segment, position) => (
            <Button
              key={segment.fromAt}
              size="small"
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              onClick={() => {
                setPlaying(false);
                setIndex(deviationRanges[position]?.from ?? 0);
              }}
            >
              {formatDateTime(segment.fromAt, locale)} · {Math.round(segment.durationSeconds / 60)}{' '}
              {t('routes.minutes')} · {segment.maxDistanceMeters} m
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Exposed so the map can colour the deviating stretches. */
export function deviationRangesOf(track: RouteTrack): { from: number; to: number }[] {
  if (!track.deviations) return [];
  return track.deviations.map((segment) => {
    const from = Math.max(
      0,
      track.pings.findIndex((ping) => ping.recordedAt >= segment.fromAt),
    );
    let to = from;
    for (let i = from; i < track.pings.length; i += 1) {
      if (track.pings[i]!.recordedAt <= segment.toAt) to = i;
    }
    return { from, to };
  });
}
